import * as Phaser from 'phaser';
import { PaintedScene } from '../PaintedScene';
import {
  installSnowTexture,
  installBreathTexture,
  createSnowfall,
  createBreathMist,
} from './frostmereAmbient';
import {
  readFrostmereRestoration,
  getRestorationTier,
  FROSTMERE_RESTORATION_KEY,
} from './frostmereRestoration';

export const NW_PEAKS_PHASER_KEY = 'NwPeaksScene' as const;
export const NW_PEAKS_MANIFEST_KEY = 's-306-nw-peaks' as const;

const AURORA_COLORS = [0x40e0a0, 0x60c0e0, 0x8080f0, 0x40e0a0] as const;
const NIGHT_TINT = 0x1a1a3a;

export class NwPeaksScene extends PaintedScene {
  private snowEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private breathEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private nightOverlay: Phaser.GameObjects.Rectangle | null = null;
  private auroraGraphics: Phaser.GameObjects.Graphics | null = null;
  private auroraVisible = false;
  private auroraAlpha = 0;
  private elapsedTime = 0;

  constructor() {
    super(NW_PEAKS_PHASER_KEY, NW_PEAKS_MANIFEST_KEY);
  }

  preload(): void {
    super.preload();
    installSnowTexture(this);
    installBreathTexture(this);
  }

  protected override onSceneReady(): void {
    this.buildNightOverlay();
    this.snowEmitter = createSnowfall(this, 0.5);

    if (this.walker) {
      this.breathEmitter = createBreathMist(this, this.walker);
    }

    this.auroraGraphics = this.add.graphics();
    this.auroraGraphics.setScrollFactor(0);
    this.auroraGraphics.setDepth(0.8);
    this.auroraGraphics.setBlendMode(Phaser.BlendModes.ADD);

    const pct = readFrostmereRestoration(this.game);
    this.auroraVisible = getRestorationTier(pct) >= 50;

    this.game.registry.events.on(
      `changedata-${FROSTMERE_RESTORATION_KEY}`,
      this.onRestorationChange,
      this,
    );
  }

  update(time: number, delta: number): void {
    super.update(time, delta);
    this.elapsedTime += delta;
    this.tickAuroraShimmer();
  }

  protected override onSceneShutdown(): void {
    this.snowEmitter?.destroy();
    this.breathEmitter?.destroy();
    this.nightOverlay?.destroy();
    this.auroraGraphics?.destroy();
    this.game.registry.events.off(
      `changedata-${FROSTMERE_RESTORATION_KEY}`,
      this.onRestorationChange,
      this,
    );
    this.snowEmitter = null;
    this.breathEmitter = null;
    this.nightOverlay = null;
    this.auroraGraphics = null;
  }

  private onRestorationChange = (_parent: unknown, value: number): void => {
    this.auroraVisible = getRestorationTier(value) >= 50;
  };

  private buildNightOverlay(): void {
    const w = this.scale.width;
    const h = this.scale.height;
    this.nightOverlay = this.add.rectangle(w / 2, h / 2, w, h, NIGHT_TINT, 0.25);
    this.nightOverlay.setScrollFactor(0);
    this.nightOverlay.setDepth(140);
    this.nightOverlay.setBlendMode(Phaser.BlendModes.MULTIPLY);
  }

  private tickAuroraShimmer(): void {
    if (!this.auroraGraphics) return;

    const targetAlpha = this.auroraVisible ? 1 : 0;
    this.auroraAlpha += (targetAlpha - this.auroraAlpha) * 0.02;
    if (this.auroraAlpha < 0.01) {
      this.auroraGraphics.clear();
      return;
    }

    const w = this.scale.width;
    const h = this.scale.height;
    const t = this.elapsedTime / 1000;

    this.auroraGraphics.clear();

    for (let band = 0; band < 3; band++) {
      const baseY = h * (0.08 + band * 0.06);
      const colorIdx = (band + Math.floor(t * 0.3)) % AURORA_COLORS.length;
      const color = AURORA_COLORS[colorIdx];
      const bandAlpha = (0.12 + Math.sin(t * 0.5 + band) * 0.04) * this.auroraAlpha;

      this.auroraGraphics.lineStyle(8 + band * 4, color, bandAlpha);
      this.auroraGraphics.beginPath();

      for (let x = 0; x <= w; x += 16) {
        const nx = x / w;
        const y = baseY + Math.sin(nx * Math.PI * 3 + t * 0.7 + band * 1.2) * (h * 0.03)
          + Math.sin(nx * Math.PI * 5 + t * 0.4) * (h * 0.015);
        if (x === 0) {
          this.auroraGraphics.moveTo(x, y);
        } else {
          this.auroraGraphics.lineTo(x, y);
        }
      }
      this.auroraGraphics.strokePath();
    }
  }
}
